import {usePersona, useCan} from 'src/permissions/hooks';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {usersApi} from 'src/api';
import {Table} from 'src/components/tables/Table';
import {PeopleOutline} from '@mui/icons-material';
import {FilterBar} from 'src/components/common/FilterBar';
import {TableLayout} from 'src/components/tables/TableLayout';
import {usersOfTariffColumnConfig} from './ClientListInTariffsInfo.tableConfig';
import {ClientListInTariffsInfoType} from './ClientListInTariffsInfo.types';
import {ClientTableCard} from '../ClientTableCard';
import {EmptyTable} from '../../tables/EmptyTable';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {useAppSelector} from 'src/app/hooks';
import {UsersSearchRequest} from 'src/types';
import {UserTypeEnum} from 'src/enums/common';

/**
 * The drivers on this tariff.
 *
 * Built from the shared TableLayout and Table, like every other list in the application. It used to
 * hand-roll the card and the footer, which put it at a different width, radius and row height from
 * the tables beside it, and left the rows-per-page control floating under nothing whenever the table
 * itself did not render.
 *
 * Hidden outright without the permission the search itself checks. A driver has no business reading
 * other people, so the request was refused and the panel stood there empty - a card promising a list
 * that could never arrive.
 */
export const ClientListInTariffsInfo: FC<ClientListInTariffsInfoType> = ({
                                                                            tariffId,
                                                                            businessId,
                                                                        }) => {
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);
    const persona = usePersona();
    const isCompanyUser = persona.isCompany;
    const canReadUsers = useCan('users.search');
    const [getUsers, users] = usersApi.useLazyGetUsersQuery();
    const [perPage, setPerPage] = useState(100);
    const [currentPage, setCurrentPage] = useState(0);

    useEffect(() => {
        if (!tariffId || !businessId || !canReadUsers) {
            return;
        }

        const input: UsersSearchRequest = {
            data: {
                tariffId,
                businessId,
                userTypes: [UserTypeEnum.PRIVATE_DRIVER],
            },
            params: {size: perPage, page: currentPage},
        };

        if (isCompanyUser) {
            input.data.companyId = authInfo?.companyId;
        }

        getUsers(input);
    }, [tariffId, businessId, perPage, currentPage, canReadUsers]);

    const handlePerPageChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const val = +e.target.value;
        if (typeof val === 'number') {
            setPerPage(val);
            setCurrentPage(0);
        }
    };

    if (!canReadUsers) {
        return null;
    }

    const rows = users?.data?.content ?? [];

    return (
        <TableLayout
            toolbar={(
                <FilterBar
                    title={intl.formatMessage({id: 'clientsOnThisPlan'})}
                    icon={<PeopleOutline/>}
                    subtitle={intl.formatMessage(
                        {id: 'rowsFound'},
                        {count: users?.data?.page?.totalElements ?? 0}
                    )}
                />
            )}
            totalPages={users?.data?.page?.totalPages ?? 0}
            page={currentPage}
            pageSize={perPage}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePerPageChange}
        >
            <ResponsiveTable
                responsiveElement={
                    <>
                        <CardList>
                            {rows.map((item) => (
                                <ClientTableCard
                                    key={item?.id}
                                    {...item}
                                />
                            ))}
                        </CardList>
                        {!rows.length && <EmptyTable/>}
                    </>
                }
                desktopElement={
                    <Table
                        rows={rows}
                        columns={usersOfTariffColumnConfig}
                        hideFooter
                    />
                }
            />
        </TableLayout>
    );
};
